// Timezone-aware date/time formatting using Intl.DateTimeFormat.
//
// The previous date-fns format() helpers rendered using the runtime's local
// timezone — UTC on the Node server, the user's TZ in the browser. That
// produced React hydration mismatches on SSR'd public pages and wrong-time
// strings everywhere a Vercel function disagreed with the user's locale.
//
// All date/time text in the app should now go through these helpers and pass
// the offering's `timezone` field (e.g. "Africa/Johannesburg"), which is the
// stable source of truth for when the offering happens.

const DEFAULT_TZ = "Africa/Johannesburg";

function tz(timeZone?: string | null) {
  return timeZone && timeZone.length > 0 ? timeZone : DEFAULT_TZ;
}

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

// Internal: build an Intl.DateTimeFormat for `en-GB` (matches the date-fns
// `EEEE d MMMM yyyy` / `HH:mm` style we used before — Saturday-first weekdays,
// 24h time, DD before MM).
function fmt(timeZone: string, options: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat("en-GB", { ...options, timeZone });
}

/** "Saturday 12 May 2026" */
export function formatLongDate(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  return fmt(tz(timeZone), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/** "12 May 2026" */
export function formatMediumDate(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  return fmt(tz(timeZone), {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/** "Sat 12 May 2026" */
export function formatShortDate(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  return fmt(tz(timeZone), {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

/** "Sat 12 May" — short form without year */
export function formatShortDateNoYear(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  return fmt(tz(timeZone), {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
}

/** "Sat 12" — weekday + day only */
export function formatWeekdayDay(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  return fmt(tz(timeZone), {
    weekday: "short",
    day: "numeric",
  }).format(date);
}

/** "12 May" — short month + day, no year */
export function formatDayMonth(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  return fmt(tz(timeZone), {
    day: "numeric",
    month: "short",
  }).format(date);
}

/** "10:00" */
export function formatTime(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  return fmt(tz(timeZone), {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

/** "12 May 10:00" — used in /money + registrations tables for paid-at lines.
 *  Mirrors the old `format(d, "d MMM HH:mm")` shape (one space, no comma). */
export function formatMonthDayTime(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  const tzName = tz(timeZone);
  // Intl doesn't have a single token for "d MMM HH:mm", so build it from parts.
  const dayPart = fmt(tzName, { day: "numeric", month: "short" }).format(date);
  const timePart = formatTime(date, tzName);
  return `${dayPart} ${timePart}`;
}

// =====================================================================
// Calendar-day comparisons in a fixed timezone.
// =====================================================================
//
// The session schedule formatter needs to know things like "are these two
// dates the same calendar day in the offering's timezone?" — Date.getDay()
// uses the runtime's local TZ, which gives wrong answers near midnight.

/**
 * Returns the calendar parts (year, month, day, weekday) of an instant in a
 * specific timezone. Use this instead of Date.getDay() / .getMonth() / .getFullYear()
 * whenever the comparison depends on a fixed location.
 */
export function getCalendarParts(value: string | Date, timeZone?: string | null) {
  const date = toDate(value);
  const tzName = tz(timeZone);
  const parts = fmt(tzName, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    weekday: "short",
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  // weekday → 0..6 (Sunday=0) so we can index WEEKDAY_PLURALS in sessions.ts
  const dowMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    weekday: dowMap[get("weekday")] ?? 0,
  };
}

/** Same calendar day in `timeZone`? */
export function isSameDayInTZ(
  a: string | Date,
  b: string | Date,
  timeZone?: string | null
) {
  const ap = getCalendarParts(a, timeZone);
  const bp = getCalendarParts(b, timeZone);
  return ap.year === bp.year && ap.month === bp.month && ap.day === bp.day;
}

// =====================================================================
// Spans — a start and an end that may fall on different days.
// =====================================================================
//
// A residential retreat or a conference is one continuous block: attendees
// arrive at 16:00 on Friday and leave at 14:00 on Sunday. There is no daily
// time to quote, so these helpers render the two ends of the block instead.
// Everything is keyed off the offering's timezone, like the rest of this file.

type SpanStyle = "long" | "short";

function nameParts(value: string | Date, timeZone: string, style: SpanStyle) {
  const date = toDate(value);
  const parts = fmt(timeZone, {
    weekday: style,
    day: "numeric",
    month: style,
    year: "numeric",
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return {
    weekday: get("weekday"),
    day: get("day"),
    month: get("month"),
    year: get("year"),
  };
}

/** Whole calendar days from `start`'s day to `end`'s day in `timeZone`:
 *  0 for the same day, 2 for Friday → Sunday. DST shifts are absorbed by
 *  rounding since the count is taken between local midnights. */
export function calendarDaysBetween(
  start: string | Date,
  end: string | Date,
  timeZone?: string | null
): number {
  const a = getCalendarParts(start, timeZone);
  const b = getCalendarParts(end, timeZone);
  const aMs = Date.UTC(a.year, a.month - 1, a.day);
  const bMs = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((bMs - aMs) / 86_400_000);
}

/** Do `start` and `end` fall on different calendar days in `timeZone`? A
 *  missing end never spans. */
export function spansDays(
  start: string | Date,
  end: string | Date | null | undefined,
  timeZone?: string | null
): boolean {
  if (!end) return false;
  return !isSameDayInTZ(start, end, timeZone);
}

/**
 * Calendar range for a block that crosses days, with the shared parts said
 * once:
 *
 *   same month → "Friday 15 – Sunday 17 May 2026"
 *   same year  → "Friday 29 May – Sunday 1 June 2026"
 *   else       → "Thursday 31 December 2026 – Saturday 2 January 2027"
 *
 * `style: "short"` abbreviates weekday and month ("Fri 15 – Sun 17 May 2026").
 */
export function formatDateRange(
  start: string | Date,
  end: string | Date,
  timeZone?: string | null,
  style: SpanStyle = "long"
): string {
  const tzName = tz(timeZone);
  const a = nameParts(start, tzName, style);
  const b = nameParts(end, tzName, style);
  const right = `${b.weekday} ${b.day} ${b.month} ${b.year}`;
  if (a.year !== b.year) {
    return `${a.weekday} ${a.day} ${a.month} ${a.year} – ${right}`;
  }
  if (a.month !== b.month) {
    return `${a.weekday} ${a.day} ${a.month} – ${right}`;
  }
  return `${a.weekday} ${a.day} – ${right}`;
}

/** The two clock times of a block that crosses days, each pinned to its
 *  weekday: "16:00 Fri – 14:00 Sun". */
export function formatSpanTimes(
  start: string | Date,
  end: string | Date,
  timeZone?: string | null
): string {
  const tzName = tz(timeZone);
  const a = nameParts(start, tzName, "short");
  const b = nameParts(end, tzName, "short");
  return `${formatTime(start, tzName)} ${a.weekday} – ${formatTime(end, tzName)} ${b.weekday}`;
}

/**
 * The two ends of a block that crosses days, each a complete moment, for a
 * layout that stacks them:
 *
 *   { start: "Thursday 20 August, 18:30",
 *     end:   "Friday 21 August 2026, 19:30" }
 *
 * The year sits on the end alone when both share it — it's the same year,
 * said once, at the end of the reading.
 */
export function formatSpanEnds(
  start: string | Date,
  end: string | Date,
  timeZone?: string | null,
  style: SpanStyle = "long"
): { start: string; end: string } {
  const tzName = tz(timeZone);
  const a = nameParts(start, tzName, style);
  const b = nameParts(end, tzName, style);
  const left =
    a.year === b.year
      ? `${a.weekday} ${a.day} ${a.month}`
      : `${a.weekday} ${a.day} ${a.month} ${a.year}`;
  return {
    start: `${left}, ${formatTime(start, tzName)}`,
    end: `${b.weekday} ${b.day} ${b.month} ${b.year}, ${formatTime(end, tzName)}`,
  };
}

/**
 * One line for any start/end pair, whatever its shape:
 *
 *   no end     → "Saturday 12 May 2026, 10:00"
 *   same day   → "Saturday 12 May 2026, 10:00–13:00"
 *   spans days → "Friday 15 May, 16:00 – Sunday 17 May 2026, 14:00"
 *
 * `style: "short"` abbreviates weekday and month for lists and emails.
 */
export function formatSpan(
  start: string | Date,
  end: string | Date | null | undefined,
  timeZone?: string | null,
  style: SpanStyle = "long"
): string {
  const tzName = tz(timeZone);
  const dateOf = style === "long" ? formatLongDate : formatShortDate;
  if (!end) return `${dateOf(start, tzName)}, ${formatTime(start, tzName)}`;
  if (!spansDays(start, end, tzName)) {
    return `${dateOf(start, tzName)}, ${formatTime(start, tzName)}–${formatTime(end, tzName)}`;
  }
  // Both sides are built from parts (not formatLongDate) so they read the
  // same whatever punctuation the runtime's ICU puts after the weekday.
  const ends = formatSpanEnds(start, end, tzName, style);
  return `${ends.start} – ${ends.end}`;
}
